import { useState, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Calendar, Camera, AlertTriangle, UserCheck, X, Edit, Package, Truck } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

const NotificationCenter = ({ user, activeModule }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [notificationMeta, setNotificationMeta] = useState({
    unread_count: 0,
    grouped_counts: { week: 0, month: 0, archive: 0 },
    unread_comments_count: 0,
    unread_demandes_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [pendingValidations, setPendingValidations] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  
  // Edit modal state
  const [editModal, setEditModal] = useState({
    open: false,
    schedule: null,
    isAmazon: true,
    tour_type: '',
    delivered: 0,
    returned: 0,
    picked: 0,
    admin_note: '',
    saving: false
  });
  
  // Check if current user is admin
  const isAdmin = user?.role === 'admin';
  
  // Amazon tour types
  const TOUR_TYPES = ['NEXT', 'SDA', 'SDB', 'SDC', 'SDA+C', 'SDA+B', 'SDC+B'];

  useEffect(() => {
    fetchNotifications();
    if (isAdmin) {
      fetchPendingValidations();
    }
  }, [isAdmin]);

  const fetchNotifications = async () => {
    try {
      console.log('🔔 Fetching notifications from:', `${API}/notifications`);
      const response = await axios.get(`${API}/notifications`, {
        withCredentials: true
      });
      console.log('🔔 Received notifications:', response.data);
      // Handle both array response and object with notifications array
      const notificationsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.notifications || []);
      console.log('🔔 Number of notifications:', notificationsData.length);
      setNotifications(notificationsData);
      if (!Array.isArray(response.data)) {
        setNotificationMeta({
          unread_count: response.data?.unread_count || 0,
          grouped_counts: response.data?.grouped_counts || { week: 0, month: 0, archive: 0 },
          unread_comments_count: response.data?.unread_comments_count || 0,
          unread_demandes_count: response.data?.unread_demandes_count || 0
        });
      } else {
        setNotificationMeta((prev) => ({
          ...prev,
          unread_count: notificationsData.filter((n) => !n.read).length
        }));
      }
    } catch (error) {
      console.error('❌ Failed to fetch notifications:', error);
      console.error('❌ Error details:', error.response?.data);
      setNotifications([]); // Set empty array on error to prevent crash
      setNotificationMeta({
        unread_count: 0,
        grouped_counts: { week: 0, month: 0, archive: 0 },
        unread_comments_count: 0,
        unread_demandes_count: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingValidations = async () => {
    try {
      const response = await axios.get(`${API}/schedule/pending-validations`, {
        withCredentials: true
      });
      setPendingValidations(response.data);
      console.log('📋 Pending validations:', response.data.length);
    } catch (error) {
      console.error('Failed to fetch pending validations:', error);
    }
  };

  // Determine if a schedule is for Colis Privé or Amazon
  const isColisPriveSchedule = (schedule) => {
    const siteName = (schedule?.site_name || '').toLowerCase();
    const tourType = (schedule?.tour_type || '').toLowerCase();
    const comment = (schedule?.comment || '').toLowerCase();
    
    return siteName.includes('colis') || siteName.includes('privé') || siteName.includes('prive') ||
           tourType.includes('colis') || comment.includes('livrés:') || comment.includes('retours:');
  };

  // Open edit modal for a mission
  const openEditModal = async (notification) => {
    // Find the schedule from pending validations that matches this notification
    const userName = notification.message.match(/^(.+?)\s+a terminé/)?.[1]?.trim();
    let schedule = pendingValidations.find(s => s.user_name === userName);
    
    // If not found, refresh pending validations and try again
    if (!schedule) {
      console.log('🔄 Refreshing pending validations...');
      try {
        const response = await axios.get(`${API}/schedule/pending-validations`, {
          withCredentials: true
        });
        setPendingValidations(response.data);
        schedule = response.data.find(s => s.user_name === userName);
      } catch (error) {
        console.error('Failed to refresh pending validations:', error);
      }
    }
    
    if (!schedule) {
      console.log('❌ Mission non trouvée dans les validations en attente');
      console.log('Available pending validations:', pendingValidations);
      console.log('Looking for user:', userName);
      
      // Mark the notification as read since the mission is no longer pending
      await markAsRead(notification.notification_id);
      
      alert(`Cette mission a déjà été traitée ou n'est plus en attente de validation.\n\nLa notification a été marquée comme lue.`);
      return;
    }
    
    const isColis = isColisPriveSchedule(schedule);
    
    // Parse existing values from schedule
    const existingDelivered = schedule.delivered || 0;
    const existingReturned = schedule.returned || 0;
    const existingPicked = schedule.picked || 0;
    
    setEditModal({
      open: true,
      schedule: schedule,
      notification: notification,
      isAmazon: !isColis,
      tour_type: schedule.tour_type || '',
      delivered: existingDelivered,
      returned: existingReturned,
      picked: existingPicked,
      admin_note: '',
      saving: false
    });
  };

  // Close edit modal
  const closeEditModal = () => {
    setEditModal({
      open: false,
      schedule: null,
      notification: null,
      isAmazon: true,
      tour_type: '',
      delivered: 0,
      returned: 0,
      picked: 0,
      admin_note: '',
      saving: false
    });
  };

  // Save edits and approve mission
  const saveAndApproveMission = async () => {
    if (!editModal.schedule) return;
    
    setEditModal(prev => ({ ...prev, saving: true }));
    
    try {
      // First, save any edits
      const editData = {};
      if (editModal.isAmazon && editModal.tour_type) {
        editData.tour_type = editModal.tour_type;
      } else if (!editModal.isAmazon) {
        editData.delivered = editModal.delivered;
        editData.returned = editModal.returned;
        editData.picked = editModal.picked;
      }
      if (editModal.admin_note) {
        editData.admin_note = editModal.admin_note;
      }
      
      // Save edits if any changes were made
      if (Object.keys(editData).length > 0) {
        await axios.put(
          `${API}/schedule/${editModal.schedule.schedule_id}/edit-before-approval`,
          editData,
          { withCredentials: true }
        );
        console.log('✅ Edits saved');
      }
      
      // Then approve the mission
      await axios.post(
        `${API}/schedule/${editModal.schedule.schedule_id}/validate`,
        { action: 'approve' },
        { withCredentials: true }
      );
      
      // Mark notification as read
      if (editModal.notification) {
        await markAsRead(editModal.notification.notification_id);
      }
      
      // Refresh data
      await fetchNotifications();
      await fetchPendingValidations();
      
      closeEditModal();
      alert('✅ Mission validée avec succès !');
      
    } catch (error) {
      console.error('Failed to save/approve:', error);
      alert(`❌ Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setEditModal(prev => ({ ...prev, saving: false }));
    }
  };

  // Reject mission from modal
  const rejectMissionFromModal = async () => {
    if (!editModal.schedule) return;
    
    const reason = prompt('Raison du rejet (optionnel):');
    
    setEditModal(prev => ({ ...prev, saving: true }));
    
    try {
      await axios.post(
        `${API}/schedule/${editModal.schedule.schedule_id}/validate`,
        { action: 'reject', reason: reason || 'Rejeté par l\'administration' },
        { withCredentials: true }
      );
      
      if (editModal.notification) {
        await markAsRead(editModal.notification.notification_id);
      }
      
      await fetchNotifications();
      await fetchPendingValidations();
      
      closeEditModal();
      alert('❌ Mission rejetée');
      
    } catch (error) {
      console.error('Failed to reject:', error);
      alert(`❌ Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setEditModal(prev => ({ ...prev, saving: false }));
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        withCredentials: true
      });
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Failed to mark as read:', error);
      toast.error('Notification non marquée comme lue : vérifiez la connexion puis réessayez');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        withCredentials: true
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error(`Notifications non marquées comme lues : ${typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    }
  };

  const handleDemandeAction = async (notification, action) => {
    try {
      console.log('🔄 Handling demande action:', action, notification.message);
      
      // Extract user name from notification message
      // Format: "Gabriel Onana Molo demande X jour(s) de repos"
      const userMatch = notification.message.match(/^(.+?)\s+demande\s+\d+/);
      if (!userMatch) {
        console.error('Cannot extract user from notification:', notification.message);
        alert('❌ Impossible d\'identifier l\'utilisateur de la demande');
        return;
      }
      
      const userName = userMatch[1].trim();
      console.log('👤 Extracted user name:', userName);
      
      // Get all pending demandes
      const demandesResponse = await axios.get(`${API}/demandes/pending`, { withCredentials: true });
      console.log('📋 All pending demandes:', demandesResponse.data);
      
      // Find the user's pending demande
      const userDemande = demandesResponse.data.find(d => 
        d.user_name === userName && d.status === 'pending'
      );
      
      if (!userDemande) {
        console.error('No pending demande found for user:', userName);
        alert(`❌ Aucune demande en attente trouvée pour ${userName}`);
        return;
      }
      
      console.log('✅ Found demande:', userDemande.demande_id);
      
      // Make API call to approve/reject
      const endpoint = action === 'approve' ? 'approve' : 'reject';
      const comment = action === 'approve' ? 'Approuvé depuis les notifications' : 'Rejeté depuis les notifications';
      
      await axios.put(`${API}/demandes/${userDemande.demande_id}/${endpoint}`, {
        comment: comment
      }, {
        withCredentials: true
      });
      
      // Mark notification as read and refresh
      await markAsRead(notification.notification_id);
      await fetchNotifications();
      
      const actionText = action === 'approve' ? 'approuvée' : 'rejetée';
      alert(`✅ Demande de ${userName} ${actionText} avec succès !`);
      console.log(`✅ Demande ${actionText} for:`, userName);
      
    } catch (error) {
      console.error(`❌ Failed to ${action} demande:`, error);
      const actionText = action === 'approve' ? 'approuver' : 'rejeter';
      alert(`❌ Erreur: Impossible de ${actionText} la demande: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleValidationAction = async (notification, action) => {
    try {
      console.log('🔄 Handling validation action:', action, notification.message);
      
      const actionText = action === 'approve' ? 'validée' : 'rejetée';
      
      // Mark notification as read and refresh
      await markAsRead(notification.notification_id);
      await fetchNotifications();
      
      alert(`✅ Mission ${actionText} avec succès !`);
      console.log(`✅ Mission ${actionText} for:`, notification.message);
      
    } catch (error) {
      console.error(`❌ Failed to ${action} validation:`, error);
      alert(`❌ Erreur: Impossible de ${action === 'approve' ? 'valider' : 'rejeter'} la mission`);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'planning_update': return <Calendar className="w-5 h-5 text-purple-500" />;
      case 'photo_reminder': return <Camera className="w-5 h-5 text-blue-500" />;
      case 'incident_update': return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'approval': return <UserCheck className="w-5 h-5 text-green-500" />;
      default: return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getPeriodGroup = (notification) => {
    if (notification.period_group) return notification.period_group;
    const createdAt = new Date(notification.created_at);
    const now = new Date();
    const ageDays = Math.max(0, Math.floor((now - createdAt) / (1000 * 60 * 60 * 24)));
    if (ageDays > 90) return 'archive';
    if (ageDays <= 7) return 'week';
    return 'month';
  };

  const groupedNotifications = {
    week: notifications.filter((n) => n.status !== 'processed' && n.status !== 'archived' && getPeriodGroup(n) === 'week'),
    month: notifications.filter((n) => n.status !== 'processed' && n.status !== 'archived' && getPeriodGroup(n) === 'month'),
    archive: notifications.filter((n) => n.status === 'processed' || n.status === 'archived' || getPeriodGroup(n) === 'archive')
  };

  const activeNotifications = notifications.filter((n) => n.status !== 'processed' && n.status !== 'archived');
  const archivedNotifications = notifications.filter((n) => n.status === 'processed' || n.status === 'archived');

  const renderNotificationCard = (notification) => (
    <Card
      key={notification.notification_id}
      onClick={() => {
        if (!notification.target_url) return;
        navigate(notification.target_url);
        markAsRead(notification.notification_id);
      }}
      className={`transition-colors ${!notification.read ? 'bg-blue-50 border-blue-100' : ''} ${notification.target_url ? 'cursor-pointer hover:border-blue-400 hover:shadow-md' : ''}`}
      data-testid={`notification-card-${notification.notification_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            notification.type === 'planning_update' ? 'bg-purple-100' :
            notification.type === 'photo_reminder' ? 'bg-blue-100' :
            notification.type === 'incident_update' ? 'bg-orange-100' :
            notification.type === 'approval' ? 'bg-green-100' : 'bg-gray-100'
          }`}>
            {getIcon(notification.type)}
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <p className={`font-medium ${!notification.read ? 'text-[#0F172A]' : 'text-gray-700'}`}>
                  {notification.title}
                </p>
                <p className="text-gray-600 text-sm mt-1">{notification.message}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <p className="text-gray-400 text-xs">
                    {new Date(notification.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                  {notification.is_special_amazon_mission && (
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-300" data-testid={`notification-special-amz-${notification.notification_id}`}>
                      Mission spéciale Amazon
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && notification.type === 'demande' && !notification.read && notification.status !== 'processed' && notification.status !== 'archived' && (
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md flex items-center gap-1"
                      onClick={() => handleDemandeAction(notification, 'approve')}
                      data-testid={`notif-approve-demande-${notification.notification_id}`}
                    >
                      <Check className="w-4 h-4" />
                      Accepter
                    </button>
                    <button
                      className="px-3 py-1 border border-red-300 text-red-600 hover:bg-red-50 text-sm rounded-md flex items-center gap-1"
                      onClick={() => handleDemandeAction(notification, 'reject')}
                      data-testid={`notif-reject-demande-${notification.notification_id}`}
                    >
                      <X className="w-4 h-4" />
                      Refuser
                    </button>
                  </div>
                )}

                {isAdmin && notification.type === 'validation_request' && !notification.read && notification.status !== 'processed' && notification.status !== 'archived' && (
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md flex items-center gap-1"
                      onClick={() => openEditModal(notification)}
                      data-testid={`notif-validate-mission-${notification.notification_id}`}
                    >
                      <Edit className="w-4 h-4" />
                      Vérifier & Valider
                    </button>
                  </div>
                )}

                {!notification.read && notification.status !== 'processed' && notification.status !== 'archived' && !['demande', 'validation_request'].includes(notification.type) && (
                  <button
                    className="px-2 py-1 text-gray-400 hover:text-gray-600 text-sm"
                    onClick={() => markAsRead(notification.notification_id)}
                    data-testid={`notif-mark-read-${notification.notification_id}`}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const unreadCount = notificationMeta.unread_count || activeNotifications.filter(n => !n.read).length;

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A] font-['Chivo']">Notifications</h1>
            <p className="text-gray-500">
              {unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? 's' : ''}` : 'Tout est lu'}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={markAllAsRead} data-testid="mark-all-read-btn">
              <CheckCheck className="w-4 h-4 mr-2" />
              Tout marquer comme lu
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="notifications-summary-grid">
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-gray-500">Semaine</p><p className="text-xl font-bold text-blue-700">{notificationMeta.grouped_counts?.week ?? groupedNotifications.week.length}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-gray-500">Mois</p><p className="text-xl font-bold text-indigo-700">{notificationMeta.grouped_counts?.month ?? groupedNotifications.month.length}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-gray-500">Archives (&gt;3 mois)</p><p className="text-xl font-bold text-slate-700">{notificationMeta.grouped_counts?.archive ?? groupedNotifications.archive.length}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-gray-500">Repos non lues</p><p className="text-xl font-bold text-amber-700">{notificationMeta.unread_demandes_count || 0}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-gray-500">Commentaires non lus</p><p className="text-xl font-bold text-red-700">{notificationMeta.unread_comments_count || 0}</p></CardContent></Card>
        </div>

        <div className="flex gap-2" data-testid="notifications-view-tabs">
          <button
            onClick={() => setActiveTab('active')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${activeTab === 'active' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-slate-200 text-slate-600'}`}
            data-testid="notifications-tab-active"
          >
            Actives ({activeNotifications.length})
          </button>
          <button
            onClick={() => setActiveTab('archive')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${activeTab === 'archive' ? 'bg-slate-100 border-slate-300 text-slate-700' : 'bg-white border-slate-200 text-slate-600'}`}
            data-testid="notifications-tab-archive"
          >
            Archives ({archivedNotifications.length})
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
          </div>
        ) : activeTab === 'active' && activeNotifications.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Aucune notification active</p>
            </CardContent>
          </Card>
        ) : activeTab === 'archive' && archivedNotifications.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Aucune notification archivée</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {activeTab === 'active' ? (
              [
                { key: 'week', label: 'Cette semaine', data: groupedNotifications.week },
                { key: 'month', label: 'Ce mois-ci', data: groupedNotifications.month }
              ].map((section) => (
                section.data.length > 0 ? (
                  <section key={section.key} className="space-y-3" data-testid={`notification-section-${section.key}`}>
                    <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{section.label} ({section.data.length})</h2>
                    <div className="space-y-3">
                      {section.data.map((notification) => renderNotificationCard(notification))}
                    </div>
                  </section>
                ) : null
              ))
            ) : (
              <section className="space-y-3" data-testid="notification-section-archive">
                <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Archives ({archivedNotifications.length})</h2>
                <div className="space-y-3">
                  {archivedNotifications.map((notification) => renderNotificationCard(notification))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {/* Edit Mission Modal */}
      {editModal.open && editModal.schedule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className={`p-4 border-b ${editModal.isAmazon ? 'bg-blue-50' : 'bg-purple-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {editModal.isAmazon ? (
                    <Truck className="w-6 h-6 text-blue-600" />
                  ) : (
                    <Package className="w-6 h-6 text-purple-600" />
                  )}
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">
                      Validation de mission
                    </h3>
                    <p className="text-sm text-gray-600">
                      {editModal.isAmazon ? '🚚 Amazon' : '📦 Colis Privé'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeEditModal}
                  className="p-1 hover:bg-gray-200 rounded-full"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Employee Info */}
            <div className="p-4 bg-gray-50 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-lg font-bold text-white">
                    {editModal.schedule.user_name?.charAt(0) || '?'}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{editModal.schedule.user_name}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(editModal.schedule.date).toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Current Data */}
            <div className="p-4 border-b">
              <p className="text-sm font-medium text-gray-500 mb-2">Données déclarées par l&apos;employé :</p>
              <div className="bg-gray-100 rounded-lg p-3 text-sm">
                {editModal.schedule.comment || 'Aucune donnée'}
              </div>
            </div>

            {/* Edit Form */}
            <div className="p-4 space-y-4">
              <p className="text-sm font-medium text-gray-700">
                ✏️ Modifier les données avant validation :
              </p>

              {editModal.isAmazon ? (
                /* Amazon Form - Tour Type */
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Type de tournée
                  </label>
                  <select
                    value={editModal.tour_type}
                    onChange={(e) => setEditModal(prev => ({ ...prev, tour_type: e.target.value }))}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">-- Sélectionner --</option>
                    {TOUR_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              ) : (
                /* Colis Privé Form - Delivery Stats */
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Colis livrés
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={editModal.delivered}
                      onChange={(e) => setEditModal(prev => ({ ...prev, delivered: parseInt(e.target.value) || 0 }))}
                      className="text-center text-lg font-semibold"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Retours
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={editModal.returned}
                      onChange={(e) => setEditModal(prev => ({ ...prev, returned: parseInt(e.target.value) || 0 }))}
                      className="text-center text-lg font-semibold"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ramassés
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={editModal.picked}
                      onChange={(e) => setEditModal(prev => ({ ...prev, picked: parseInt(e.target.value) || 0 }))}
                      className="text-center text-lg font-semibold"
                    />
                  </div>
                </div>
              )}

              {/* Admin Note */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Note admin (optionnel)
                </label>
                <textarea
                  value={editModal.admin_note}
                  onChange={(e) => setEditModal(prev => ({ ...prev, admin_note: e.target.value }))}
                  placeholder="Ex: Corrigé NEXT → SDA suite à erreur de saisie"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 resize-none h-20"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 border-t bg-gray-50 flex gap-3">
              <button
                onClick={rejectMissionFromModal}
                disabled={editModal.saving}
                className="flex-1 px-4 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                Rejeter
              </button>
              <button
                onClick={saveAndApproveMission}
                disabled={editModal.saving}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {editModal.saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                {editModal.saving ? 'Enregistrement...' : 'Enregistrer & Valider'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default NotificationCenter;
